import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { Loader2 } from "lucide-react";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import ReferralSignups from "../components/ReferralSignups";
import EmailPreferences from "../components/EmailPreferences";
import { fetchMyReferral, fetchSignups, applyForProgram, referralLink, partnerKitLink, formatMoney, MyReferral, ReferredAccount } from "../utils/referral";

export const PARTNER_TYPES = [
  { value: "av-production", label: "AV company, integrator or event production company" },
  { value: "event-organizers", label: "Event organizer" },
  { value: "nonprofits", label: "Non-profit organization" },
];

const Referrals: React.FC = () => {
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const [info, setInfo] = useState<MyReferral | null>(null);
  const [accounts, setAccounts] = useState<ReferredAccount[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ partnerName: "", partnerType: "av-production", company: "", details: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    if (!user) return;
    fetchMyReferral(user)
      .then((mine) => {
        setInfo(mine);
        if (mine.status === "approved") fetchSignups(user).then(setAccounts).catch(() => setError("Could not load your referred accounts."));
      })
      .catch(() => setError("Could not load your referral information. Please try again in a moment."))
      .finally(() => setReady(true));
  };
  useEffect(load, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.displayName) setForm((f) => (f.partnerName ? f : { ...f, partnerName: user.displayName as string }));
  }, [user]);

  const link = info?.code ? referralLink(info.code) : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      await applyForProgram(user, form);
      setReady(false);
      load();
    } catch {
      setError("Could not send your application. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3 text-sm";
  const card = "bg-white rounded-3xl p-6 shadow-sm border border-slate-100";

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="referrals" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <header>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Referrals</h1>
              <p className="text-slate-600 mt-1 text-lg">Refer customers to ConferenceCaptioning and earn rewards.</p>
              {info?.isAdmin && (
                <p className="mt-2"><Link className="text-blue-600 underline" to="/referrals-admin">Review applications and partners</Link></p>
              )}
            </header>

            {(loading || !ready) && !error ? (
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            ) : !info ? (
              <p className="text-red-700" role="alert">{error}</p>
            ) : info.status === "none" ? (
              <section className={card} aria-labelledby="apply-heading">
                <h2 id="apply-heading" className="text-xl font-bold text-slate-900">Apply to become a partner</h2>
                <p className="text-slate-600 text-sm mt-1">
                  We review every application by hand. Read how the program works at{" "}
                  <a className="text-blue-600 underline" href="https://conferencecaptioning.com/partners/">conferencecaptioning.com/partners</a>.
                </p>
                <form onSubmit={submit} className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="ref-name" className="font-bold text-slate-900 block mb-1">Your name</label>
                    <input id="ref-name" required maxLength={120} className={field} value={form.partnerName} onChange={(e) => setForm({ ...form, partnerName: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="ref-type" className="font-bold text-slate-900 block mb-1">Partner type</label>
                    <select id="ref-type" className={field} value={form.partnerType} onChange={(e) => setForm({ ...form, partnerType: e.target.value })}>
                      {PARTNER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ref-company" className="font-bold text-slate-900 block mb-1">Company or organization</label>
                    <input id="ref-company" required maxLength={160} className={field} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="ref-details" className="font-bold text-slate-900 block mb-1">How will you refer customers, and how would you like to be paid?</label>
                    <textarea id="ref-details" required rows={5} maxLength={2000} className={field} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
                      placeholder="For example: I add captions to client shows. Cash payout by wire transfer or PayPal. Non-profits: credits or a donation." />
                  </div>
                  {error && <p className="text-red-700 text-sm" role="alert">{error}</p>}
                  <button type="submit" disabled={submitting} className="btn-primary">{submitting ? "Sending..." : "Send application"}</button>
                </form>
              </section>
            ) : info.status === "pending" ? (
              <section className={card}>
                <h2 className="text-xl font-bold text-slate-900">Application received</h2>
                <p className="text-slate-600 text-sm mt-1">We are reviewing it. When it is approved, your link and numbers will appear here.</p>
              </section>
            ) : info.status === "suspended" ? (
              <section className={card}>
                <h2 className="text-xl font-bold text-slate-900">Your partner account is paused</h2>
                <p className="text-slate-600 text-sm mt-1">Your link is not recording new referrals right now. Contact us if you have questions.</p>
              </section>
            ) : (
              <>
                <section className={card} aria-labelledby="link-heading">
                  <h2 id="link-heading" className="text-xl font-bold text-slate-900">Your link</h2>
                  <p className="text-slate-600 text-sm mt-1">
                    Share this link, or have customers enter the code <strong>{info.code}</strong> when they sign up or on a demo call.
                    A customer who creates a new account within 180 days of clicking it is credited to you.
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                    <input readOnly aria-label="Your referral link" value={link} className="flex-1 bg-slate-50 border border-slate-300 rounded-xl py-2 px-3 font-mono text-sm" onFocus={(e) => e.currentTarget.select()} />
                    <button onClick={copy} className="btn-primary">{copied ? "Copied" : "Copy"}</button>
                  </div>
                </section>
                <section className="grid grid-cols-3 gap-4" aria-label="Referral numbers">
                  <div className={card}><div className="text-4xl font-bold text-slate-900">{info.visits}</div><p className="text-slate-600 text-sm mt-1">Visits through your link</p></div>
                  <div className={card}><div className="text-4xl font-bold text-slate-900">{info.signups}</div><p className="text-slate-600 text-sm mt-1">Accounts created</p></div>
                  <div className={card}><div className="text-4xl font-bold text-slate-900">{formatMoney(info.earned ?? 0)}</div><p className="text-slate-600 text-sm mt-1">Earned so far</p></div>
                </section>
                <section className={card} aria-labelledby="kit-heading">
                  <h2 id="kit-heading" className="text-xl font-bold text-slate-900">Partner resources</h2>
                  <p className="text-slate-600 text-sm mt-1">
                    Texts you can say in podcasts and posts, a QR code for your link, and videos you can add your own voiceover to.
                  </p>
                  <p className="mt-3">
                    <a className="btn-primary inline-block" href={partnerKitLink(info.code as string)} target="_blank" rel="noopener noreferrer">Open the partner kit</a>
                  </p>
                </section>
                {user && <EmailPreferences user={user} initial={info.emailPreferences} />}
                <section className={card} aria-labelledby="accounts-heading">
                  <h2 id="accounts-heading" className="text-xl font-bold text-slate-900 mb-3">Accounts you referred</h2>
                  {error && <p className="text-red-700 text-sm" role="alert">{error}</p>}
                  <ReferralSignups accounts={accounts} />
                </section>
                <p className="text-slate-600 text-sm">
                  Rewards are worked out and paid by us after a referred customer has paid for their first event or first year, plus 10% of what they pay over the following 24 months, so these numbers are not a payout statement.
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Referrals;
